// libs
import classNames from 'classnames'
import * as React from 'react'

// interfaces / constants
import { CLASS_PREFIX } from 'src/constants/'
import { IKeyValue } from 'src/interfaces/'

// components
import SelectFieldItemSegment from 'src/components/item_segment/layouts/select_field/select_field'
import Popover from 'src/components/popover/popover'
import { Tag } from 'src/components/tag/tag'

// helpers
import { textResources } from 'src/lang/de'
import './multi-select-field.scss'

export interface IMultiSelectOption {
  id: string;
  name: string;
}

interface IProps {
  assistiveText?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  errorText?: string;
  placeholder?: string;

  name: string;

  options: IMultiSelectOption[];

  /** Controlled selected values */
  values?: IMultiSelectOption[];
  /** Uncontrolled initial selected values */
  defaultValues?: IMultiSelectOption[];

  /** Called when selected values change */
  onChange?: (values: IMultiSelectOption[]) => void;

  /** Called whenever the text input changes (e.g. to filter `options`) */
  onTextChange?: (value: string) => void;
}

interface IState {
  isTouched: boolean;
  hasFocus: boolean;
  innerText: string;
  innerValues: IMultiSelectOption[];
}

const clsBlock: string = CLASS_PREFIX + 'multi-select-field'

const uniqueById = (values: IMultiSelectOption[]): IMultiSelectOption[] => {
  const map = new Map<string, IMultiSelectOption>()
  values.forEach((v) => {
    map.set(v.id, v)
  })
  return Array.from(map.values())
}

const removeValueById = (values: IMultiSelectOption[], id: string): IMultiSelectOption[] =>
  values.filter((v) => v.id !== id)

export const MultiSelectField: React.FC<IProps> = (props) => {
  const {
    assistiveText,
    className,
    disabled,
    label,
    required,
    errorText,
    placeholder,
    name,
    options,
    values,
    defaultValues,
    onChange,
    onTextChange
  } = props

  const inputRef = React.useRef<HTMLInputElement>(null)

  // refs to show / hide popover from inside effects; needed to ensure that the popover shows whenever we get options while focused
  // TODO: refactor Popover to provide better control over its open state
  const popoverShowRef = React.useRef<(() => void) | null>(null)
  const popoverHideRef = React.useRef<(() => void) | null>(null)

  const [state, setState] = React.useState<IState>({
    isTouched: false,
    hasFocus: false,
    innerText: '',
    innerValues: defaultValues || []
  })

  const selectedValues = values !== undefined ? values : state.innerValues

  React.useEffect(() => {
    if (values !== undefined) {
      setState((s) => ({ ...s, innerValues: values }))
    }
  }, [values])

  const selectedIdSet = React.useMemo(
    () => new Set<string>(selectedValues.map((v) => v.id)),
    [selectedValues]
  )

  const availableOptions = React.useMemo(
    () => options.filter((o) => !selectedIdSet.has(o.id)),
    [options, selectedIdSet]
  )

  const hasAvailableOptions = availableOptions.length > 0

  React.useEffect(() => {
    if (!state.hasFocus) return

    if (hasAvailableOptions) {
      popoverShowRef.current?.()
      return
    }

    popoverHideRef.current?.()
  }, [hasAvailableOptions, state.hasFocus])

  const isEmpty = selectedValues.length === 0 && !state.innerText.trim()

  const invalidState = (!!errorText) || (required && selectedValues.length === 0 && state.isTouched)
  const errorMessage = required && selectedValues.length === 0 ? textResources.shared.required : errorText
  const showErrorMessage = !!errorMessage && invalidState
  const showAssistiveMessage = (assistiveText && invalidState && !showErrorMessage) || (assistiveText && !invalidState)

  const cls: IKeyValue = {
    [clsBlock]: true,
    [`${className}`]: !!className,
    [`${clsBlock}--invalid`]: invalidState,
    [`${clsBlock}--touched`]: state.isTouched,
    [`${clsBlock}--empty`]: isEmpty
  }

  const clsBody: IKeyValue = {
    [`${clsBlock}__body`]: true,
    [`${clsBlock}__body--invalid`]: invalidState,
    [`${clsBlock}__body--disabled`]: !!disabled
  }

  const clsLabel: IKeyValue = {
    [`${clsBlock}__label`]: true,
    [`${clsBlock}__label--disabled`]: !!disabled,
    [`${clsBlock}__label--static-label`]: state.hasFocus || !isEmpty || !!placeholder
  }

  const updateSelectedValues = (nextValues: IMultiSelectOption[]) => {
    const uniqueValues = uniqueById(nextValues)

    if (values === undefined) {
      setState((s) => ({ ...s, innerValues: uniqueValues }))
    }

    onChange?.(uniqueValues)
  }

  const onFocus = () => {
    if (disabled) return
    setState((s) => ({ ...s, hasFocus: true }))

    if (hasAvailableOptions) {
      popoverShowRef.current?.()
    }
  }

  const onBlur = () => {
    setState((s) => ({ ...s, hasFocus: false, isTouched: true }))
  }

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value
    setState((s) => ({ ...s, innerText: next }))
    onTextChange?.(next)

    if (hasAvailableOptions) {
      popoverShowRef.current?.()
    }
  }

  const clearText = () => {
    setState((s) => ({ ...s, innerText: '' }))
    onTextChange?.('')
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return

    if (e.key === 'Backspace' && !state.innerText) {
      const last = selectedValues[selectedValues.length - 1]
      if (last) {
        updateSelectedValues(removeValueById(selectedValues, last.id))
      }
    }
  }

  const focusInput = () => {
    if (disabled) return
    inputRef.current?.focus()
  }

  const onRemoveSelected = (idToRemove: string) => (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    updateSelectedValues(removeValueById(selectedValues, idToRemove))
    focusInput()
  }

  const onSelectOption = (option: IMultiSelectOption) => () => {
    updateSelectedValues([...selectedValues, { id: option.id, name: option.name }])
    clearText()
    focusInput()
  }

  const inputId = `${clsBlock}__${name}`

  const renderField = (hide?: () => void) => (
    <div className={classNames(cls)}>
      <div className={classNames(clsBody)} onClick={focusInput}>
        <div className={`${clsBlock}__inner-body`}>
          <div className={`${clsBlock}__chips-input`}>
            {selectedValues.map((v) => (
              <Tag
                key={v.id}
                text={v.name}
                gradient
                className={`${clsBlock}__tag`}
                onRemove={disabled ? undefined : onRemoveSelected(v.id)}
              />
            ))}
            <input
              id={inputId}
              ref={inputRef}
              className={`${clsBlock}__input`}
              value={state.innerText}
              onChange={onInputChange}
              onKeyDown={(e) => {
                onKeyDown(e)
                if (e.key === 'Escape' && hide) {
                  hide()
                }
              }}
              onFocus={onFocus}
              onBlur={onBlur}
              disabled={disabled}
              placeholder={selectedValues.length ? '' : placeholder}
              aria-label={label || name}
              type="text"
              name={name}
            />
          </div>
          <label className={classNames(clsLabel)} htmlFor={inputId}>
            {label}
          </label>
        </div>
      </div>
      {showErrorMessage && <div className={`${clsBlock}__error-text`}>{errorMessage}</div>}
      {showAssistiveMessage && <div className={`${clsBlock}__assistive-text`}>{assistiveText}</div>}
    </div>
  )

  const renderPopover = () => (
    <div style={{ position: 'relative' }}>
      <Popover
        className={classNames(
          clsBlock + '__popover',
          !hasAvailableOptions && (clsBlock + '__popover--empty')
        )}
        position='bottom'
        align='relative'
        layout='plain'
        trigger={({ hide, show }) => {
          popoverHideRef.current = hide
          popoverShowRef.current = show
          return renderField(hide)
        }}
        disabled={disabled}
      >
        {({ hide, callAll }) => (
          <ul className={`${clsBlock}__list`}>
            {availableOptions.map((option: IMultiSelectOption) => (
              <li key={option.id} className={clsBlock + '__list-item'}>
                <SelectFieldItemSegment
                  label={option.name}
                  navigation={{ onClick: callAll(onSelectOption(option), hide) }}
                />
              </li>
            ))}
          </ul>
        )}
      </Popover>
    </div>
  )

  return disabled ? renderField() : renderPopover()
}

MultiSelectField.displayName = 'MultiSelectField'
